import * as PIXI from "pixi.js";
import data from "../configs/data";
import {Button} from "../controlElements/Button";
import {Utils} from "../Utils";
import {OptionsMenu} from "./OptionsMenu";
import {SpinButton} from "./SpinButton";

export class RouletteButtonsPanel extends PIXI.Container {

    private removeBetButton: Button;
    private optionsButton: Button;
    private optionsMenu: OptionsMenu;
    private homeButton: Button;
    private removeAllBetsButton: Button;
    private menuButton: Button;
    private quickSpinButton: Button;
    private restoreBetsButton: Button;
    private spinButton: SpinButton;

    private readonly HOME_BUTTON_POSITION = new PIXI.Point(97, 0);
    private readonly OPTIONS_BUTTON_POSITION = new PIXI.Point(205, 0);
    private readonly OPTIONS_MENU_POSITION = new PIXI.Point(205, -102);
    private readonly REMOVE_BET_BUTTON_POSITION = new PIXI.Point(312, 0);
    private readonly REMOVE_ALL_BETS_BUTTON_POSITION = new PIXI.Point(420, 0);
    private readonly MENU_BUTTON_POSITION = new PIXI.Point(969, 0);
    private readonly QUICK_SPIN_BUTTON_POSITION = new PIXI.Point(1076, 0);
    private readonly REBET_BUTTON_POSITION = new PIXI.Point(861, 0);
    private readonly SPIN_BUTTON_POSITION = new PIXI.Point(1201, 0);

    constructor() {
        super();
        this.createAndAddHomeButton();
        this.createAndOptionsMenu();
        this.createAndAddOptionsButton();
        this.createAndAddRemoveBetButton();
        this.createAndAddRemoveAllBetsButton();
        this.createAndAddMenuButton();
        this.createAndAddQuickSpinButton();
        this.createAndAddRebetButton();
        this.createAndAddSpinButton();
    }

    public subscribeOnRemoveBetButtonClicked(subscriber: Function): void {
        this.removeBetButton.subscribeOnClicked(subscriber);
    }

    public subscribeOnSpinButtonClicked(subscriber: Function): void {
        this.spinButton.subscribeOnClicked(subscriber);
    }

    public subscribeOnQuickSpinButtonClicked(subscriber: Function): void {
        this.quickSpinButton.subscribeOnClicked(subscriber);
    }

    public subscribeOnRemoveAllBetsButtonClicked(subscriber: Function): void {
        this.removeAllBetsButton.subscribeOnClicked(subscriber);
    }

    public subscribeOnMenuButtonClicked(subscriber: Function): void {
        this.menuButton.subscribeOnClicked(subscriber);
    }

    public subscribeOnRestoreBetsButtonClicked(subscriber: Function): void {
        this.restoreBetsButton.subscribeOnClicked(subscriber);
    }

    public setOnSpinButtonSpinsLeft(number: number): void {
        this.spinButton.setSpinsLeftValue(number);
    }

    public playLoadingAnimationOnSpinButton(): void {
        this.spinButton.playLoadingAnimation();
    }

    public playIdleAnimationOnSpinButton(): void {
        this.spinButton.playIdleAnimation();
    }

    public disableAllButtons(): void {
        this.removeBetButton.enabled = false;
        this.optionsButton.enabled = false;
        this.homeButton.enabled = false;
        this.removeAllBetsButton.enabled = false;
        this.menuButton.enabled = false;
        this.quickSpinButton.enabled = false;
        this.restoreBetsButton.enabled = false;
        this.spinButton.disable();
    }

    public enableAllButtons(): void {
        this.removeBetButton.enabled = true;
        this.optionsButton.enabled = true;
        this.homeButton.enabled = true;
        this.removeAllBetsButton.enabled = true;
        this.menuButton.enabled = true;
        this.quickSpinButton.enabled = true;
        this.restoreBetsButton.enabled = true;
        this.spinButton.enable();
    }

    public enableSpinButtons(): void {
        this.spinButton.enable();
        this.quickSpinButton.enabled = true;
        this.menuButton.enabled = true;
    }

    public disableSpinButtons(): void {
        this.spinButton.disable();
        this.quickSpinButton.enabled = false;
        this.menuButton.enabled = false;
    }

    private createAndAddSpinButton(): void {
        this.spinButton = new SpinButton();
        this.spinButton.position = this.SPIN_BUTTON_POSITION;
        (window as any).spinButton = this.spinButton;
        this.addChild(this.spinButton);
    }

    private createAndOptionsMenu(): void {
        this.optionsMenu = new OptionsMenu();
        this.optionsMenu.position = this.OPTIONS_MENU_POSITION;
        this.addChild(this.optionsMenu);
        this.optionsMenu.visible = false;
    }

    private createAndAddRemoveBetButton(): void {
        this.removeBetButton = new Button(data.images.removeBetButton, data.images.removeBetButtonOnHover,
            data.images.removeBetButtonOnClick, data.images.removeBetButtonInActive,
            data.audios.buttonClicked);
        this.removeBetButton.position = this.REMOVE_BET_BUTTON_POSITION;
        this.addChild(this.removeBetButton);
    }

    private createAndAddMenuButton(): void {
        this.menuButton = new Button(data.images.menuBase, data.images.menuHover,
            data.images.menuClick, data.images.menuInActive,
            data.audios.buttonClicked);
        this.menuButton.position = this.MENU_BUTTON_POSITION;
        this.addChild(this.menuButton);
    }

    private createAndAddQuickSpinButton(): void {
        this.quickSpinButton = new Button(data.images.quickSpinBase, data.images.quickSpinHover,
            data.images.quickSpinClick, data.images.quickSpinInActive,
            data.audios.buttonClicked);
        this.quickSpinButton.position = this.QUICK_SPIN_BUTTON_POSITION;
        (window as any).quickSpinButton = this.quickSpinButton;
        this.addChild(this.quickSpinButton);
    }

    private createAndAddRebetButton(): void {
        this.restoreBetsButton = new Button(data.images.rebetBase, data.images.rebetHover,
            data.images.rebetClick, data.images.rebetInActive,
            data.audios.buttonClicked);
        this.restoreBetsButton.position = this.REBET_BUTTON_POSITION;
        (window as any).rebetButton = this.restoreBetsButton;
        this.addChild(this.restoreBetsButton);
    }

    private createAndAddOptionsButton(): void {
        this.optionsButton = new Button(data.images.optionsBase, data.images.optionsHover,
            data.images.optionsClick, data.images.optionsInActive,
            data.audios.buttonClicked);
        this.optionsButton.position = this.OPTIONS_BUTTON_POSITION;
        this.optionsButton.subscribeOnClicked(() => {
            this.optionsMenu.visible = !this.optionsMenu.visible;
        });
        this.addChild(this.optionsButton);
    }

    private createAndAddHomeButton(): void {
        this.homeButton = new Button(data.images.homeBase, data.images.homeHover,
            data.images.homeClick, data.images.homeInActive,
            data.audios.buttonClicked);
        this.homeButton.position = this.HOME_BUTTON_POSITION;
        this.homeButton.subscribeOnClicked(() => {
            Utils.redirectToCasinoPage();
        });
        this.addChild(this.homeButton);
    }

    private createAndAddRemoveAllBetsButton(): void {
        this.removeAllBetsButton = new Button(data.images.removeAllBetsButton, data.images.removeAllBetsOnHoverButton,
            data.images.removeAllBetsButtonOnClick, data.images.removeAllBetsButtonInActive,
            data.audios.buttonClicked);
        this.removeAllBetsButton.position = this.REMOVE_ALL_BETS_BUTTON_POSITION;
        this.addChild(this.removeAllBetsButton);
    }
}



// WEBPACK FOOTER //
// ./src/app/userInterface/RouletteButtonsPanel.ts